import { Bus, Dumbbell, Monitor, Train } from 'lucide-react'

import { AndroidIcon, AppleIcon } from '@/components/icons/BrandIcons'
import { SectionLabel } from '@/features/landing/components/SectionLabel'

const CROSS_DEVICE_BEATS = [
  {
    icon: Bus,
    label: 'On the bus',
    body: 'Approve a join request, ping the squad about tonight.',
  },
  {
    icon: Dumbbell,
    label: 'At the gym',
    body: 'Start the tournament so sign-ups open the moment you’re home.',
  },
  {
    icon: Train,
    label: 'On the train',
    body: 'Override a disputed match result with the full audit trail in hand.',
  },
] as const

export function MobileFirst() {
  return (
    <section
      id="mobile"
      className="relative border-b border-border/60 px-6 py-24 md:py-32"
    >
      <div className="mx-auto grid max-w-6xl items-center gap-16 lg:grid-cols-2">
        {/* Left: copy */}
        <div className="flex flex-col gap-6">
          <SectionLabel index="07" label="Platform" />
          <h2 className="font-display text-4xl leading-[1.05] font-semibold text-balance md:text-6xl">
            Built for the phone. <br />
            <span className="gradient-text">Served on the web.</span>
          </h2>
          <p className="max-w-lg text-base text-fg-muted text-pretty md:text-lg">
            One account. One squadron. One bracket. e404 is mobile-native
            on Android and full-featured on the web, so squad business
            never has to wait for you to be at a desk.
          </p>

          {/* USP beats — concrete moments that prove the "cross-device,
              never miss a beat" claim. Three specific situations beat one
              vague "anywhere, anytime" line. */}
          <ul className="mt-2 flex flex-col gap-3">
            {CROSS_DEVICE_BEATS.map((beat) => (
              <li
                key={beat.label}
                className="flex items-start gap-3 rounded-lg border border-border bg-surface/60 p-3"
              >
                <span
                  aria-hidden
                  className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-bg/60 text-primary"
                >
                  <beat.icon className="size-4" strokeWidth={1.75} />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-sm font-semibold text-fg">
                    {beat.label}
                  </p>
                  <p className="text-[12px] text-fg-muted text-pretty">
                    {beat.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          {/* Platform availability — kept as a small footer-strip so the
              section's main job (selling the cross-device behaviour) isn't
              upstaged by a roadmap grid. */}
          <ul className="mt-2 grid grid-cols-3 gap-2 border-t border-border/60 pt-5">
            {[
              { icon: AndroidIcon, label: 'Android', detail: 'Live' },
              { icon: AppleIcon, label: 'iOS', detail: 'Q2 2026' },
              { icon: Monitor, label: 'Web', detail: 'Alpha', stroked: true },
            ].map((platform) => (
              <li
                key={platform.label}
                className="flex items-center gap-2"
              >
                <platform.icon
                  className="size-4 text-fg-muted"
                  {...(platform.stroked ? { strokeWidth: 1.5 } : {})}
                  aria-hidden
                />
                <div className="min-w-0">
                  <p className="font-display text-[12px] font-semibold text-fg">
                    {platform.label}
                  </p>
                  <p className="font-display text-[9px] tracking-[0.2em] text-fg-subtle uppercase">
                    {platform.detail}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: app-accurate screen mockup inside a phone bezel */}
        <div className="relative flex items-center justify-center">
          <div
            aria-hidden
            className="absolute -inset-20 -z-10 opacity-25 blur-3xl"
            style={{ background: 'var(--gradient-accent)' }}
          />
          <PhoneFrame
            src="/screenshots/tournament-detail.png"
            alt="The e404 Android app — tournament detail for the Ironwake Clan Cup: lock-in countdown, 14 of 16 slots filled, in-house field and roster."
            caption="App · Tournament view — demo data"
          />
        </div>
      </div>
    </section>
  )
}

/**
 * Phone-bezel frame around an app-accurate screen mockup (fictional demo
 * data — see e404_infra/marketing/mockups.html; the images render the
 * Android system UI themselves, so the frame is just the hardware
 * silhouette). width/height reserve the layout box before the lazy image
 * arrives — the #for-leaders email deep-link anchors straight to one of
 * these, so a late ~660px reflow would shift the page under the reader.
 */
export function PhoneFrame({
  src,
  alt,
  caption = 'App — demo data',
}: {
  src: string
  alt: string
  /** Small `//` caption under the device. The screens are app-accurate
   *  mockups populated with fictional demo data, so the caption must not
   *  claim they're live captures — keep "demo data" in whatever you pass. */
  caption?: string
}) {
  return (
    <div className="relative w-full max-w-[300px] shrink-0">
      <div className="relative rounded-[2.75rem] border border-border-strong bg-surface-raised p-2.5 shadow-2xl shadow-black/60 ring-1 ring-black/40">
        {/* Speaker pip — sits above the screen, decorative */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-1.5 z-10 mx-auto h-1 w-12 rounded-full bg-border"
        />
        <img
          src={src}
          alt={alt}
          width={1236}
          height={2745}
          loading="lazy"
          decoding="async"
          className="block h-auto w-full rounded-[2.25rem] bg-bg"
        />
      </div>

      {/* Subtle caption beneath the device, mirrors the in-app `//` motif. */}
      <p className="mt-5 text-center font-display text-[10px] tracking-[0.3em] text-fg-subtle uppercase">
        <span className="text-primary">//</span> {caption}
      </p>
    </div>
  )
}
